import { useCallback } from 'react';
import { safeLocalStorage } from '@toss/storage';
import { atom, useAtomValue, useSetAtom } from 'jotai';

import { useEffectOnce } from '@/hooks';

import { useBuildInfoContext } from '../context/BuildInfoProvider';
import { useBuildFormContext } from '../form/schema';
import { useMapTemplateValidation } from '../util';

import {
  type PersistBuildFormType,
  usePersistBuildForm,
} from './usePersistBuildForm';
import { useValidCategory } from './useValidCategory';

const persistButtonVisibleAtom = atom<boolean>(false);

export const useSetPersistButtonVisible = () => {
  return useSetAtom(persistButtonVisibleAtom);
};

export const usePersistButtonVisible = () => {
  return useAtomValue(persistButtonVisibleAtom);
};

export const useConfirmPersistData = () => {
  const { action, mapTemplates } = useBuildInfoContext();
  const { persistData } = usePersistBuildForm();
  const { setValue } = useBuildFormContext();
  const setIsPersistButtonVisible = useSetPersistButtonVisible();

  useEffectOnce(() => {
    setIsPersistButtonVisible(!!persistData.data && action === 'CREATE');
  });

  const { getValidateCategoryFields } = useValidCategory();

  const isEnabledMapTemplate = useMapTemplateValidation(
    persistData.data?.templateId ?? '',
    mapTemplates,
  );

  const handleConfirmPersistData = useCallback(
    (persistData: PersistBuildFormType) => {
      const { data } = persistData;

      try {
        if (data) {
          setValue('title', data.title ?? '', {
            shouldDirty: true,
            shouldValidate: false,
            shouldTouch: true,
          });
          setValue('isAllowPass', data.isAllowPass || false);
          setValue('quizList', data.quizList ?? []);
          getValidateCategoryFields(data).forEach(field => {
            setValue(field, data[field] || '');
          });
          if (isEnabledMapTemplate) {
            setValue('templateId', data.templateId || '');
            setValue('templateType', data.templateType || '');
          }
          setValue('materials', data.materials ?? []);
        }
      } catch (error) {
        // 스키마 변경으로 인한 type 에러 시 임시 저장 데이터 삭제
        safeLocalStorage.remove('quiz_build_v3');
        console.error(error);
      } finally {
        setIsPersistButtonVisible(false);
      }
    },
    [
      isEnabledMapTemplate,
      getValidateCategoryFields,
      setValue,
      setIsPersistButtonVisible,
    ],
  );

  return {
    persistData,
    handleConfirmPersistData,
  };
};
